import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Alert,
  Autocomplete,
  Button,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import DialogSimple from '@/components/dialog/DialogSimple';
import {
  FormProvider,
  RHFDatepicker,
  RHFTextField,
} from '@/components/hook-form';
import { getSettingsData } from '@/features/app/api';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import useResponsive from '@/hooks/useResponsive';
import {
  createServiceBlockedDate,
  getServiceBlockedDate,
  getVariants,
  updateServiceBlockedDate,
} from '../api';
import {
  BLOCKED_DATE_DEFAULT_VALUES,
  BLOCKED_DATE_SCHEMA,
  BLOCKED_DATES_QUERY_KEY,
  DAY_OF_WEEK_OPTIONS,
  buildServiceBlockedDatePayload,
  getBlockedDateServiceVariantName,
} from '../utils';
import {
  ServiceBlockedDateFormValues,
  ServiceBlockedDatePayload,
} from '../types';
import { City } from '@/types';
import { Variant } from '../types/variants';

type BlockedDateDialogProps = {
  isOpen: boolean;
  onClose: VoidFunction;
  currentBlockedDateId?: string;
};

type CityOption = Pick<City, 'id' | 'name'> | { id: ''; name: string };

type VariantOption = {
  id: number | '';
  label: string;
};

const backendFieldMap: Record<string, keyof ServiceBlockedDateFormValues> = {
  service_variant_id: 'service_variant_id',
  serviceVariantId: 'service_variant_id',
  city_id: 'city_id',
  cityId: 'city_id',
  start_date: 'start_date',
  startDate: 'start_date',
  end_date: 'end_date',
  endDate: 'end_date',
  day_of_the_week: 'day_of_the_week',
  dayOfTheWeek: 'day_of_the_week',
};

const extractBackendMessage = (error: any) => {
  if (typeof error === 'string') return error;
  if (typeof error?.message === 'string') return error.message;
  if (typeof error?.error === 'string') return error.error;
  return 'Something went wrong';
};

const getBackendErrors = (error: any) => {
  if (error?.errors) return error.errors;
  if (error?.data?.errors) return error.data.errors;
  return null;
};

export default function BlockedDateDialog({
  isOpen,
  onClose,
  currentBlockedDateId,
}: BlockedDateDialogProps) {
  const belowLg = useResponsive('down', 'lg');
  const { errorMsg, successMsg } = useSnackbarMsg();
  const queryClient = useQueryClient();
  const [serverError, setServerError] = useState('');

  const methods = useForm<ServiceBlockedDateFormValues>({
    defaultValues: BLOCKED_DATE_DEFAULT_VALUES,
    resolver: yupResolver(BLOCKED_DATE_SCHEMA),
  });

  const {
    control,
    handleSubmit,
    setError,
    setValue,
    reset: resetForm,
    formState: { isSubmitting, errors },
  } = methods;

  const { start_date } = useWatch({ control });

  useEffect(() => {
    if (isOpen && !currentBlockedDateId) {
      resetForm(BLOCKED_DATE_DEFAULT_VALUES);
      setServerError('');
    }
  }, [currentBlockedDateId, isOpen, resetForm]);

  const { data: settings, isLoading: isSettingsDataLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: getSettingsData,
    onError: (error) =>
      errorMsg(error, 'Something went wrong while fetching settings data'),
    refetchOnWindowFocus: false,
    enabled: isOpen,
  });

  const { data: serviceVariants, isLoading: isServiceVariantsLoading } =
    useQuery({
      queryKey: ['serviceVariants'],
      queryFn: getVariants,
      onError: (error) =>
        errorMsg(error, 'Something went wrong while fetching service variants'),
      refetchOnWindowFocus: false,
      enabled: isOpen,
    });

  const cityOptions = useMemo<CityOption[]>(
    () => [{ id: '', name: 'All cities' }, ...(settings?.cities || [])],
    [settings?.cities]
  );

  const serviceVariantOptions = useMemo<VariantOption[]>(
    () => [
      { id: '', label: 'All variants' },
      ...(serviceVariants || []).map((variant: Variant) => ({
        id: variant.id,
        label: `${
          variant.service?.name ?? 'N/A'
        } / ${getBlockedDateServiceVariantName(variant)}`,
      })),
    ],
    [serviceVariants]
  );

  const retrieveCityOption = (cityId: number | string) =>
    cityOptions.find((city) => String(city.id) === String(cityId)) ?? null;

  const retrieveVariantOption = (variantId: number | string) =>
    serviceVariantOptions.find(
      (variant) => String(variant.id) === String(variantId)
    ) ?? null;

  const { data: currentBlockedDate, isLoading: isCurrentBlockedDateLoading } =
    useQuery({
      queryKey: ['serviceBlockedDate', currentBlockedDateId],
      queryFn: () => getServiceBlockedDate(String(currentBlockedDateId)),
      onSuccess: (apiResponse) => {
        resetForm({
          city_id: apiResponse.city_id ?? '',
          service_variant_id: apiResponse.service_variant_id ?? '',
          day_of_the_week:
            apiResponse.day_of_the_week === null ||
            apiResponse.day_of_the_week === undefined
              ? ''
              : apiResponse.day_of_the_week === 7
              ? 0
              : apiResponse.day_of_the_week,
          start_date: apiResponse.start_date,
          end_date: apiResponse.end_date,
        });
        setServerError('');
      },
      onError: (error) =>
        errorMsg(error, 'Something went wrong while fetching blocked date'),
      enabled: isOpen && !!currentBlockedDateId,
      refetchOnWindowFocus: false,
    });

  const saveBlockedDateMutation = useMutation({
    mutationFn: (payload: ServiceBlockedDatePayload) =>
      currentBlockedDateId
        ? updateServiceBlockedDate(currentBlockedDateId, payload)
        : createServiceBlockedDate(payload),
    onSuccess: () => {
      queryClient.invalidateQueries([BLOCKED_DATES_QUERY_KEY]);
      successMsg(
        `Blocked date successfully ${
          currentBlockedDateId ? 'updated' : 'created'
        }`
      );
      handleCloseDialog();
    },
    onError: (error) => {
      applyBackendErrors(error);
    },
  });

  const applyBackendErrors = (error: any) => {
    const backendErrors = getBackendErrors(error);
    let fieldErrorApplied = false;

    if (
      backendErrors &&
      typeof backendErrors === 'object' &&
      !Array.isArray(backendErrors)
    ) {
      Object.entries(backendErrors as Record<string, unknown>).forEach(
        ([field, value]) => {
          const mappedField = backendFieldMap[field];
          if (!mappedField) return;

          const message = Array.isArray(value)
            ? value.join('\n')
            : typeof value === 'string'
            ? value
            : JSON.stringify(value);

          setError(mappedField, {
            type: 'server',
            message,
          });
          fieldErrorApplied = true;
        }
      );
    }

    setServerError(
      fieldErrorApplied
        ? 'Please review the highlighted fields.'
        : extractBackendMessage(error)
    );
  };

  const handleCloseDialog = () => {
    resetForm(BLOCKED_DATE_DEFAULT_VALUES);
    setServerError('');
    onClose();
  };

  const submitBlockedDate = (data: ServiceBlockedDateFormValues) => {
    setServerError('');
    saveBlockedDateMutation.mutate(buildServiceBlockedDatePayload(data));
  };

  const stackDirection = belowLg ? 'column' : 'row';
  const stackSpacing = belowLg ? 2 : 1;
  const showSkeleton =
    isSettingsDataLoading ||
    isServiceVariantsLoading ||
    !settings ||
    !serviceVariants ||
    (currentBlockedDateId && isCurrentBlockedDateLoading) ||
    (currentBlockedDateId && !currentBlockedDate);

  return (
    <DialogSimple
      dialogTitle={`${currentBlockedDateId ? 'Edit' : 'Add'} blocked date`}
      open={isOpen}
      onClickButtonClose={handleCloseDialog}
      loadingButton={false}
      disagreeButton={false}
      maxWidth="md"
    >
      <FormProvider
        methods={methods}
        onSubmit={handleSubmit(submitBlockedDate)}
      >
        {!showSkeleton ? (
          <Stack spacing={belowLg ? 2 : 1.5} sx={{ px: 3, py: 1.5 }}>
            {serverError && <Alert severity="error">{serverError}</Alert>}

            <Stack direction={stackDirection} spacing={stackSpacing}>
              <Controller
                name="city_id"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Autocomplete
                    fullWidth
                    value={retrieveCityOption(field.value)}
                    options={cityOptions}
                    noOptionsText="No such city"
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) =>
                      String(option.id) === String(value.id)
                    }
                    onChange={(_event, newValue) =>
                      setValue('city_id', newValue ? newValue.id : '', {
                        shouldValidate: true,
                      })
                    }
                    renderOption={(props, city) => (
                      <MenuItem {...props} key={city.id || 'all-cities'}>
                        {city.name}
                      </MenuItem>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="City"
                        fullWidth
                        autoFocus
                        size="small"
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                )}
              />

              <Controller
                name="service_variant_id"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Autocomplete
                    fullWidth
                    value={retrieveVariantOption(field.value)}
                    options={serviceVariantOptions}
                    noOptionsText="No such variant"
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) =>
                      String(option.id) === String(value.id)
                    }
                    onChange={(_event, newValue) =>
                      setValue(
                        'service_variant_id',
                        newValue ? newValue.id : '',
                        { shouldValidate: true }
                      )
                    }
                    renderOption={(props, variant) => (
                      <MenuItem {...props} key={variant.id || 'all-variants'}>
                        {variant.label}
                      </MenuItem>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Service variant"
                        fullWidth
                        size="small"
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                )}
              />
            </Stack>

            <Stack direction={stackDirection} spacing={stackSpacing}>
              <RHFDatepicker
                name="start_date"
                label="Start date"
                size="small"
                helperTextFromRhfContext={errors.start_date?.message}
              />
              <RHFDatepicker
                name="end_date"
                label="End date"
                size="small"
                minDate={start_date || undefined}
                helperTextFromRhfContext={errors.end_date?.message}
              />
            </Stack>

            <RHFTextField
              select
              name="day_of_the_week"
              label="Day of week"
              fullWidth
              size="small"
            >
              {DAY_OF_WEEK_OPTIONS.map((day) => (
                <MenuItem key={String(day.value)} value={day.value}>
                  {day.label}
                </MenuItem>
              ))}
            </RHFTextField>

            <Stack
              direction="row"
              justifyContent="flex-end"
              spacing={1.5}
              sx={{ pt: 1.5, pb: 1 }}
            >
              <Button
                variant="outlined"
                size="small"
                color="inherit"
                onClick={handleCloseDialog}
              >
                Cancel
              </Button>
              <LoadingButton
                variant="contained"
                size="small"
                type="submit"
                loading={isSubmitting || saveBlockedDateMutation.isLoading}
              >
                {currentBlockedDateId ? 'Save changes' : 'Add blocked date'}
              </LoadingButton>
            </Stack>
          </Stack>
        ) : (
          <Stack spacing={1} sx={{ px: 3, pb: 4 }}>
            {[...new Array(5)].map((_, i) => (
              <Skeleton height={40} key={i} />
            ))}
          </Stack>
        )}
      </FormProvider>
    </DialogSimple>
  );
}
